// NoMatch 模拟 API 接口
let mockUserProfile = null;
let mockChatRooms = [];
let mockMessages = {};
let mockMatchingQueue = [];

// 模拟数据生成
const generateMockUser = (id) => {
  const genders = ['male', 'female', 'prefer_not_to_say'];
  const preferences = ['male', 'female', 'any'];
  
  return {
    id: `user_${id}`,
    name: `用户${id}`,
    preferences: {
      userGender: genders[Math.floor(Math.random() * genders.length)],
      preferredGender: preferences[Math.floor(Math.random() * preferences.length)],
      // 保持向後相容性
      gender: preferences[Math.floor(Math.random() * preferences.length)],
    },
    isOnline: Math.random() > 0.3,
    avatar: `https://api.dicebear.com/7.x/avataaars/svg?seed=${id}`,
  };
};

const generateMockChatRoom = (id, partnerId) => ({
  id: `room_${id}`,
  partnerId,
  partnerName: `用户${partnerId}`,
  partnerAvatar: `https://api.dicebear.com/7.x/avataaars/svg?seed=${partnerId}`,
  createdAt: new Date(Date.now() - Math.random() * 86400000).toISOString(),
  expiresAt: new Date(Date.now() + 86400000).toISOString(),
  lastMessage: '你好！很高興認識你',
  lastMessageTime: new Date(Date.now() - Math.random() * 3600000).toISOString(),
  isActive: true,
});

const generateMockMessage = (id, senderId, content, timestamp) => ({
  id: `msg_${id}`,
  senderId,
  content,
  timestamp: timestamp || new Date().toISOString(),
  type: 'text',
});

// 初始化模拟数据
const initMockData = () => {
  if (mockChatRooms.length === 0) {
    // 创建一些模拟聊天室
    mockChatRooms = [
      generateMockChatRoom(1, 'partner_1'),
      generateMockChatRoom(2, 'partner_2'),
    ];
    
    // 为每个聊天室创建一些模拟消息
    mockChatRooms.forEach(room => {
      const roomMessages = [
        generateMockMessage(1, room.partnerId, '你好！很高興認識你', new Date(Date.now() - 3600000).toISOString()),
        generateMockMessage(2, 'current_user', '你好！我也很高興認識你', new Date(Date.now() - 3500000).toISOString()),
        generateMockMessage(3, room.partnerId, '你平常都喜歡做什麼呢？', new Date(Date.now() - 3000000).toISOString()),
        generateMockMessage(4, 'current_user', '我喜歡看書和旅行，你呢？', new Date(Date.now() - 2500000).toISOString()),
        generateMockMessage(5, room.partnerId, '我也喜歡旅行！你去過哪些地方？', new Date(Date.now() - 2000000).toISOString()),
      ];
      mockMessages[room.id] = roomMessages;
    });
  }
};

// 模拟延迟
const delay = (ms = 500) => new Promise(resolve => setTimeout(resolve, ms));

// 用户相关 API
export const saveUserProfile = async (userData) => {
  await delay();
  
  mockUserProfile = {
    id: 'current_user',
    name: userData.name,
    userGender: userData.userGender || 'prefer_not_to_say', // 添加 userGender 支援
    preferences: userData.preferences,
    avatar: `https://api.dicebear.com/7.x/avataaars/svg?seed=${userData.name}`,
    createdAt: new Date().toISOString(),
  };
  
  // 保存到 localStorage
  localStorage.setItem('noMatchUserProfile', JSON.stringify(mockUserProfile));
  
  return { success: true, user: mockUserProfile };
};

export const getUserProfile = async () => {
  await delay();
  
  // 尝试从 localStorage 加载
  const saved = localStorage.getItem('noMatchUserProfile');
  if (saved) {
    mockUserProfile = JSON.parse(saved);
  }
  
  return mockUserProfile;
};

// 匹配相关 API
export const requestMatching = async (preferences) => {
  await delay(2000); // 模拟匹配过程
  
  initMockData();
  
  // 模拟匹配成功，创建新聊天室
  const newRoomId = `room_${Date.now()}`;
  const partnerId = `partner_${Date.now()}`;
  
  const newRoom = {
    id: newRoomId,
    partnerId,
    partnerName: `新配對的用戶`,
    partnerAvatar: `https://api.dicebear.com/7.x/avataaars/svg?seed=${partnerId}`,
    createdAt: new Date().toISOString(),
    expiresAt: new Date(Date.now() + 86400000).toISOString(),
    lastMessage: '你們已經配對成功！開始聊天吧',
    lastMessageTime: new Date().toISOString(),
    isActive: true,
  };
  
  mockChatRooms.unshift(newRoom);
  
  // 创建欢迎消息
  mockMessages[newRoomId] = [
    generateMockMessage(1, 'system', '你們已經配對成功！開始聊天吧', new Date().toISOString()),
  ];
  
  return { 
    success: true, 
    matched: true, 
    roomId: newRoomId,
    partner: {
      name: newRoom.partnerName,
      avatar: newRoom.partnerAvatar,
    }
  };
};

// 聊天室相关 API
export const getChatRooms = async () => {
  await delay();
  
  initMockData();
  
  return {
    success: true,
    chatRooms: mockChatRooms.filter(room => room.isActive),
  };
};

export const getChatRoom = async (roomId) => {
  await delay();
  
  const room = mockChatRooms.find(r => r.id === roomId);
  
  if (!room) {
    throw new Error('Chat room not found');
  }
  
  return { success: true, chatRoom: room };
};

export const leaveChatRoom = async (roomId) => {
  await delay();
  
  const roomIndex = mockChatRooms.findIndex(r => r.id === roomId);
  
  if (roomIndex !== -1) {
    mockChatRooms[roomIndex].isActive = false;
  }
  
  return { success: true };
};

// 消息相关 API
export const getChatMessages = async (roomId) => {
  await delay();
  
  const messages = mockMessages[roomId] || [];
  
  return {
    success: true,
    messages: messages.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp)),
  };
};

export const sendMessage = async (roomId, message) => {
  await delay(300);
  
  const newMessage = generateMockMessage(
    Date.now(),
    'current_user',
    message,
    new Date().toISOString()
  );
  
  if (!mockMessages[roomId]) {
    mockMessages[roomId] = [];
  }
  
  mockMessages[roomId].push(newMessage);
  
  // 更新聊天室的最后消息
  const room = mockChatRooms.find(r => r.id === roomId);
  if (room) {
    room.lastMessage = message;
    room.lastMessageTime = newMessage.timestamp;
  }
  
  // 模拟对方回复（30%概率）
  if (Math.random() < 0.3) {
    setTimeout(async () => {
      const replies = [
        '哈哈，有趣！',
        '我也是這麼想的',
        '真的嗎？',
        '好棒！',
        '我懂你的意思',
        '說得對！',
      ];
      
      const replyMessage = generateMockMessage(
        Date.now() + 1,
        room.partnerId,
        replies[Math.floor(Math.random() * replies.length)],
        new Date().toISOString()
      );
      
      mockMessages[roomId].push(replyMessage);
      
      if (room) {
        room.lastMessage = replyMessage.content;
        room.lastMessageTime = replyMessage.timestamp;
      }
    }, 2000 + Math.random() * 3000);
  }
  
  return { success: true, message: newMessage };
};

// WebSocket 模拟
export const createWebSocketConnection = (roomId, onMessage, onError, onClose) => {
  // 模拟 WebSocket 连接
  const mockWs = {
    readyState: 1, // OPEN
    send: (data) => {
      // 模拟发送消息
      console.log('Mock WebSocket send:', data);
    },
    close: () => {
      console.log('Mock WebSocket close');
      if (onClose) onClose({ code: 1000, reason: 'Normal closure' });
    },
  };
  
  // 模拟定期接收消息
  const interval = setInterval(() => {
    if (Math.random() < 0.1) { // 10% 概率收到消息
      const mockMessage = {
        type: 'message',
        data: generateMockMessage(
          Date.now(),
          `partner_${Math.floor(Math.random() * 100)}`,
          '這是一條來自 WebSocket 的模擬消息',
          new Date().toISOString()
        ),
      };
      
      if (onMessage) onMessage(mockMessage);
    }
  }, 5000);
  
  // 清理函数
  mockWs.cleanup = () => {
    clearInterval(interval);
  };
  
  return mockWs;
};

// SignalR 相關 API 模擬實現
// 模擬事件監聽器管理
let mockEventListeners = {
  message: new Set(),
  connection: new Set(),
  error: new Set(),
  userJoined: new Set(),
  userLeft: new Set(),
  typing: new Set(),
  matching: new Set(),
};

// 模擬 SignalR 初始化
export const initializeSignalR = async (userId, authToken = null) => {
  await delay(1000);
  console.log('Mock SignalR initialized for user:', userId);
  
  // 模擬連接成功
  setTimeout(() => {
    mockEventListeners.connection.forEach(callback => {
      try {
        callback('connected', { connectionId: 'mock-connection-id' });
      } catch (error) {
        console.error('Mock connection listener error:', error);
      }
    });
  }, 100);
  
  return true;
};

// 模擬斷開連接
export const disconnectSignalR = async () => {
  await delay(200);
  console.log('Mock SignalR disconnected');
  
  // 清除所有監聽器
  Object.keys(mockEventListeners).forEach(key => {
    mockEventListeners[key].clear();
  });
  
  return true;
};

// 模擬加入聊天室
export const joinChatRoom = async (roomId) => {
  await delay(300);
  console.log('Mock SignalR joined room:', roomId);
  return true;
};

// 模擬離開聊天室
export const leaveChatRoomSignalR = async (roomId) => {
  await delay(300);
  console.log('Mock SignalR left room:', roomId);
  return true;
};

// 模擬發送消息
export const sendMessageSignalR = async (roomId, content, messageType = 'text') => {
  await delay(300);
  console.log('Mock SignalR send message:', { roomId, content, messageType });
  
  // 創建新消息
  const newMessage = generateMockMessage(
    Date.now(),
    'current_user',
    content,
    new Date().toISOString()
  );
  
  // 添加到消息列表
  if (!mockMessages[roomId]) {
    mockMessages[roomId] = [];
  }
  mockMessages[roomId].push(newMessage);
  
  // 通知消息監聽器
  mockEventListeners.message.forEach(callback => {
    try {
      callback(newMessage);
    } catch (error) {
      console.error('Mock message listener error:', error);
    }
  });
  
  // 模擬對方回復
  if (Math.random() < 0.4) {
    setTimeout(() => {
      const replies = [
        '哈哈，有趣！',
        '我也是這麼想的',
        '真的嗎？',
        '好棒！',
        '我懂你的意思',
        '說得對！',
        '你說得很有道理',
        '我也有同感',
      ];
      
      const replyMessage = generateMockMessage(
        Date.now() + 1,
        'partner_1',
        replies[Math.floor(Math.random() * replies.length)],
        new Date().toISOString()
      );
      
      mockMessages[roomId].push(replyMessage);
      
      // 通知消息監聽器
      mockEventListeners.message.forEach(callback => {
        try {
          callback(replyMessage);
        } catch (error) {
          console.error('Mock message listener error:', error);
        }
      });
    }, 1000 + Math.random() * 3000);
  }
  
  return true;
};

// 模擬發送打字狀態
export const sendTypingStatus = async (roomId, isTyping) => {
  // 模擬打字狀態，不需要實際處理
  console.log('Mock typing status:', { roomId, isTyping });
  return true;
};

// 模擬請求匹配
export const requestMatchingSignalR = async (preferences) => {
  await delay(2000);
  console.log('Mock SignalR matching request:', preferences);
  
  const result = {
    success: true,
    matched: true,
    roomId: `room_${Date.now()}`,
    partner: {
      name: '新配對的用戶',
      avatar: `https://api.dicebear.com/7.x/avataaars/svg?seed=${Date.now()}`,
    }
  };
  
  // 通知匹配監聽器
  mockEventListeners.matching.forEach(callback => {
    try {
      callback(result);
    } catch (error) {
      console.error('Mock matching listener error:', error);
    }
  });
  
  return result;
};

// 模擬獲取連接狀態
export const getSignalRConnectionState = () => {
  return {
    state: 'Connected',
    isAuthenticated: true,
    connectionId: 'mock-connection-id',
    currentRoomId: null,
    userId: 'current_user'
  };
};

// 事件監聽器方法
export const onMessage = (callback) => {
  mockEventListeners.message.add(callback);
  return () => mockEventListeners.message.delete(callback);
};

export const onConnection = (callback) => {
  mockEventListeners.connection.add(callback);
  return () => mockEventListeners.connection.delete(callback);
};

export const onError = (callback) => {
  mockEventListeners.error.add(callback);
  return () => mockEventListeners.error.delete(callback);
};

export const onUserJoined = (callback) => {
  mockEventListeners.userJoined.add(callback);
  return () => mockEventListeners.userJoined.delete(callback);
};

export const onUserLeft = (callback) => {
  mockEventListeners.userLeft.add(callback);
  return () => mockEventListeners.userLeft.delete(callback);
};

export const onTyping = (callback) => {
  mockEventListeners.typing.add(callback);
  return () => mockEventListeners.typing.delete(callback);
};

export const onMatching = (callback) => {
  mockEventListeners.matching.add(callback);
  return () => mockEventListeners.matching.delete(callback);
}; 